import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Divider, Flex, Heading, Icon, Text, VStack } from '@chakra-ui/react';
import { FaEnvelope } from 'react-icons/fa';
import { AccountContactMessagesGetConversationResponse } from '@netiva/classifieds-api';
import { Loader } from '@netiva/classifieds-ui';

import { MessageBubble } from './MessageBubble';
import { ReplyForm } from './ReplyForm';

export type MessageThreadProps = {
  conversation?: AccountContactMessagesGetConversationResponse;
  /** Whether a conversation is selected at all, which is not the same as one having arrived yet. */
  hasSelection: boolean;
  isLoading: boolean;
  isMarkingUnread: boolean;
  isSending: boolean;
  onMarkUnread: () => Promise<void>;
  onReply: (content: string) => Promise<void>;
};

export const MessageThread: FC<MessageThreadProps> = memo(function MessageThread({
  conversation,
  hasSelection,
  isLoading,
  isMarkingUnread,
  isSending,
  onMarkUnread,
  onReply,
}) {
  const { t } = useTranslation();

  if (!hasSelection) {
    return (
      <Flex align="center" justify="center" height="full" p={8}>
        <Text color="neutral.500">{t('messages.thread.none')}</Text>
      </Flex>
    );
  }

  // selected, but the first page of messages is still on its way
  if (!conversation) {
    return (
      <Flex align="center" justify="center" height="full" p={8}>
        <Loader isLoading />
      </Flex>
    );
  }

  return (
    // minH={0} is what lets the scrolling message area actually shrink: without it a flex child
    // refuses to go below its content height and pushes the reply form out of the card.
    <Flex direction="column" height="full" flex="1" minH={0}>
      <Flex p={4} borderBottomWidth="1px" borderColor="neutral.200" gap={4} justify="space-between" align="flex-start">
        <Box minW={0}>
          <Heading as="h2" size="sm">
            {conversation.dataObjectTitle || t('messages.thread.noAdvert')}
          </Heading>
          <Text fontSize="sm" color="neutral.600">
            {conversation.participant === 'Contact'
              ? t('messages.ownEnquiry')
              : `${conversation.contactName ? `${conversation.contactName} · ` : ''}${conversation.contactEmail}`}
          </Text>
        </Box>

        {/* For a conversation opened by accident, or one to keep on the badges as still to answer. */}
        <Button
          size="sm"
          variant="outline"
          flexShrink={0}
          leftIcon={<Icon as={FaEnvelope} />}
          isLoading={isMarkingUnread}
          onClick={onMarkUnread}
        >
          {t('messages.thread.markUnread')}
        </Button>
      </Flex>

      <Box flex="1" minH={0} overflowY="auto" p={4} position="relative">
        <Loader isLoading={isLoading} showOverlay />
        <VStack align="stretch" spacing={3}>
          {conversation.items.map((message) => (
            <MessageBubble key={message.id} message={message} participant={conversation.participant} />
          ))}
        </VStack>
      </Box>

      <Divider />

      <Box p={4}>
        <ReplyForm isSending={isSending} onSend={onReply} />
      </Box>
    </Flex>
  );
});
